<template>
  <v-menu
    v-model="openMenu"
    max-width="400"
    location="right center"
    :close-on-content-click="false"
    :offset="[offsetWidth, 0]"
    :style="{ marginLeft: offsetWidth + 'px' }"
  >
    <template v-slot:activator="{ props: activatorProps }">
      <div v-bind="activatorProps">
        <slot></slot>
      </div>
    </template>
    <template v-slot:default>
      <v-card class="ml-1 pa-3" width="240">
        <div class="d-flex pb-2 align-center justify-space-between">
          <v-icon>mdi-list-status</v-icon>
          <span class="text-h7 font-weight-bold">Select fields</span>
          <v-btn
            variant="plain"
            size="small"
            icon="mdi-close"
            @click="openMenu = false"
          />
        </div>
        <v-divider />
        <v-list density="compact" class="pt-0">
          <template v-for="(field, fieldIndex) in fields" :key="fieldIndex">
            <v-list-item v-if="!field.relatedEntityId || (isCommand && field.reference)" class="py-0 min-h-0">
              <template #prepend>
                <v-checkbox
                  v-model="field.checked"
                  hide-details
                  density="compact"
                  class="mr-2"
                />
              </template>
              <v-list-item-title class="text-sm">{{ field.name }}</v-list-item-title>
            </v-list-item>

            <v-list-group v-else :key="'group-' + fieldIndex" class="py-0 min-h-0">
              <template #activator="{ props }">
                <v-list-item v-bind="props" class="py-0 min-h-0">
                  <v-list-item-title class="text-sm">{{ field.name }}</v-list-item-title>
                </v-list-item>
              </template>

              <v-list-item
                v-for="(entityField, entityIndex) in field.entityFields"
                :key="'entity-' + entityIndex"
                class="py-0 min-h-0"
              >
                <template #prepend>
                  <v-checkbox
                    v-model="entityField.checked"
                    hide-details
                    density="compact"
                    class="mr-2"
                    :disabled="!!(isCommand && field.reference) && !entityField.checked"
                  />
                </template>
                <v-list-item-title class="text-sm">{{ entityField.name }}</v-list-item-title>
              </v-list-item>
            </v-list-group>
          </template>
        </v-list>
        <v-divider></v-divider>
        <div class="d-flex justify-center">
          <v-btn variant="plain" density="compact" @click="openMenu = false">
            Close
          </v-btn>
        </div>
      </v-card>
    </template>
  </v-menu>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

const { card, offsetWidth } = defineProps({ card: Object, offsetWidth: Number })
const emit = defineEmits(['onUpdateFields'])

const openMenu = ref(false)

const store = useStore()
const entities = computed(() => store.state.workflow.workflow.dataModels)
const isCommand = computed(() => card.cardType?.triggerType)

const fields = ref([])
let isInitialized = false

watch(
  () => fields.value,
  (newVal) => {
    if (!isInitialized) {
      isInitialized = true
      return
    }
    emit('onUpdateFields', newVal)
  },
  { deep: true }
)

watch(
  () => card,
  () => {
    const rootEntity = entities.value?.find((entity) => entity.id === card.entityId)
    const rootEntityFields = rootEntity?.dataFields || []
    const dataModelFields = card.dataModel.dataFields

    isInitialized = false
    fields.value = rootEntityFields.map(field => {
      if (!field.relatedEntityId || (isCommand.value && field.reference)) {
        return {
          ...field,
          checked: !!dataModelFields.find(df => df.name === field.name)
        }
      }

      const relatedEntity = entities.value.find(entity => entity.id === field.relatedEntityId)
      const dataModelSubFields = dataModelFields.find(df => df.name === field.name)?.dataFields?.map(f => f.name) || []
      return {
        ...field,
        entityFields: (relatedEntity?.dataFields || []).map(entityField => {
          return {
            ...entityField,
            checked: dataModelSubFields.includes(entityField.name)
          }
        })
      }
    })
  },
  { immediate: true, deep: true }
)
</script>

<style scoped>
.v-list-item {
  padding: 0px !important;
}

.v-list-item--density-compact:not(.v-list-item--nav).v-list-item--one-line {
  padding-inline: 0px;
}

.v-list-group__items .v-list-item {
  padding-inline-start: 16px !important;
}

.v-list-item--density-compact.v-list-item--one-line {
  min-height: 28px;
  &.v-list-item--prepend {
    direction: rtl;
    text-align: left;
  }
}

.v-list-group__items .v-list-item--density-compact.v-list-item--one-line {
  direction: ltr;
}

::v-deep(.v-list-item__append .v-list-item__spacer) {
  width: 8px !important;
}

.v-input--density-compact {
  --v-input-control-height: 28px;
}

.w-50 {
  width: 50%;
}
</style>
